/* eslint-disable camelcase, no-magic-numbers, newline-before-return, one-var, require-jsdoc, sort-imports, sort-vars */
import DateHelper from 'app/utilities/date-helper';
import getVideoId from 'get-video-id';
import Performance from 'app/models/performance';
import {
    APP_DESCRIPTION,
    APP_HOSTNAME,
    APP_NAME
} from 'config/app';
import { GENRES } from 'config/event';

const LANGUAGE = 'en-NZ';
const LOGO_ID = `${APP_HOSTNAME}/#logo`;
const LOGO_PATH = 'static/assets/images/horizontal-colour.81d21cfba5a3ae25a7e7.svg';
const ORG_ID = `${APP_HOSTNAME}/#organization`;
const ROOT_URL = `${APP_HOSTNAME}/`;
const NEWS_URL = `${APP_HOSTNAME}/news`;
const WEBSITE_ID = `${APP_HOSTNAME}/#website`;
const VENUE_SAME_AS = {
    'aotea-centre': [
        'https://www.wikidata.org/wiki/Q4778787',
    ],
    'aotea-square': [
        'https://www.wikidata.org/wiki/Q2857988',
    ],
    'auckland-town-hall': [
        'https://www.wikidata.org/wiki/Q1391825',
    ],
    'bruce-mason-centre': [
        'https://www.wikidata.org/wiki/Q108097277',
    ],
    'go-media-stadium': [
        'https://www.wikidata.org/wiki/Q1426996',
    ],
    'shed-10-queens-wharf': [
        'https://www.wikidata.org/wiki/Q124713889',
    ],
    'te-komititanga': [
        'https://www.wikidata.org/wiki/Q111365742'
    ],
    'the-civic': [
        'https://www.wikidata.org/wiki/Q4391509',
    ],
    'the-cloud-queens-wharf': [
        'https://www.wikidata.org/wiki/Q7723487',
    ],
    'viaduct-events-centre': [
        'https://www.wikidata.org/wiki/Q7924448',
    ],
    'western-springs-stadium': [
        'https://www.wikidata.org/wiki/Q7988322',
    ],
};
const SOCIAL_URLS = [
    'https://www.facebook.com/AklLive',
    'https://www.instagram.com/aucklandlive',
    'https://www.tiktok.com/@aucklandlive',
    'https://www.youtube.com/user/aucklandlive',
    'https://www.wikidata.org/wiki/Q17067440'
];
const PARENT_ORG = {
    '@type': 'Organization',
    '@id': 'https://www.aucklandunlimited.com/#organization',
    name: 'Auckland Unlimited',
    url: 'https://www.aucklandunlimited.com/',
    sameAs: [
        'https://www.linkedin.com/company/auckland-unlimited',
        'https://www.wikidata.org/wiki/Q55764471'
    ],
    logo: {
        '@type': 'ImageObject',
        url: 'https://aucklandunlimited.com/static/assets/images/tataki-auckland-unlimited-a-navy-rgb.26dbb9e1deac145cabbb.svg'
    }
};

export function getCanonicalUrl(path = '') {
    const slug = path.replace(/^\/+/, '').replace(/\/+$/, '');
    return slug ? `${APP_HOSTNAME}/${slug}` : ROOT_URL;
}

export function absoluteUrl(value) {
    if (!value) return undefined;
    if (Array.isArray(value)) return value.map((item) => absoluteUrl(item)).filter(Boolean);
    if (typeof value !== 'string') return value;
    if ((/^https?:\/\//i).test(value)) return value;
    if (value.startsWith('//')) return `https:${value}`;
    return value.startsWith('/') ? `${APP_HOSTNAME}${value}` : `${APP_HOSTNAME}/${value}`;
}

export function stripHtml(value = '') {
    return decodeEntities(String(value)
        .replace(/<script[\s\S]*?<\/script>/gi, ' ')
        .replace(/<style[\s\S]*?<\/style>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim());
}

export function getWebsiteStructuredData() {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        url: APP_HOSTNAME,
        name: APP_NAME,
        description: APP_DESCRIPTION,
        inLanguage: LANGUAGE,
        publisher: {
            '@type': 'Organization',
            '@id': ORG_ID,
            name: APP_NAME,
            url: APP_HOSTNAME,
            logo: {
                '@type': 'ImageObject',
                '@id': LOGO_ID,
                url: absoluteUrl(LOGO_PATH)
            }
        },
        potentialAction: {
            '@type': 'SearchAction',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: `${APP_HOSTNAME}/?search=true&keyword={search_term_string}`
            },
            'query-input': 'required name=search_term_string'
        }
    };
}

export function getGlobalStructuredData() {
    return {
        '@context': 'https://schema.org',
        '@graph': [
            organization()
        ]
    };
}

export function getPageStructuredData({ description, image, isNewsArticle, page, title, url }) {
    const attributes = page && page.attributes ? page.attributes : {};
    const pageUrl = url || getCanonicalUrl();
    const pageTitle = title || attributes.name || APP_NAME;
    const graphNodes = [
        compact({
            '@type': 'WebPage',
            '@id': `${pageUrl}#webpage`,
            url: pageUrl,
            name: pageTitle,
            description: stripHtml(description || attributes.seo_description || attributes.short_description || attributes.description),
            image: absoluteUrl(image)
        })
    ];

    if (isNewsArticle) {
        graphNodes.push(getNewsArticleStructuredData(page, { description, image, title, url: pageUrl }));
    }

    return graph(graphNodes);
}

export function getNewsCollectionStructuredData(url = NEWS_URL) {
    return withContext({
        '@type': 'CollectionPage',
        '@id': `${url}#collection`,
        url,
        name: 'Auckland Live News'
    });
}

export function getNewsArticleStructuredData(page, fallback = {}) {
    const attributes = page && page.attributes ? page.attributes : {};
    const url = fallback.url || getCanonicalUrl(`news/${attributes.slug || ''}`);
    const headline = attributes.name || fallback.title;
    if (!headline) return null;

    return compact({
        '@type': 'NewsArticle',
        '@id': `${url}#article`,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        url,
        headline,
        description: stripHtml(attributes.short_description || attributes.seo_description || fallback.description),
        image: images(attributes.thumbnail || attributes.mobile_thumbnail || attributes.landscape_thumbnail || fallback.image),
        datePublished: date(attributes.published_at || attributes.published_date || attributes.created_at),
        dateModified: date(attributes.updated_at || attributes.modified_at),
        publisher: publisher(),
        articleSection: 'News',
        inLanguage: LANGUAGE,
        isAccessibleForFree: true,
        isPartOf: {
            '@type': 'CollectionPage',
            '@id': `${NEWS_URL}#collection`,
            name: 'Auckland Live News',
            url: NEWS_URL
        }
    });
}

export function getShowStructuredData(show) {
    if (!show) return null;

    const url = absoluteUrl(show.url);
    const performances = (show.props && show.props.performances || [])
        .map((item) => new Performance(item, show.venues))
        .filter((performance) => !performance.isPast);

    if (!performances.length) return null;

    // Expand each venue once; event locations only reference the venue @id.
    const venuesById = {};
    const events = performances.map((performance) => {
        const venue = performance.getVenueObject();
        if (venue) {
            const id = venueId(venue);
            if (!venuesById[id]) {
                venuesById[id] = venuePlace(venue);
            }
        }
        return performanceEvent(show, performance, url);
    });

    return graph([
        ...Object.keys(venuesById).map((id) => venuesById[id]),
        ...events
    ]);
}

export function getEventStructuredData(event) {
    if (!event || !event.name) return null;

    const props = event.props || {};
    const seo = event.seo || {};
    const thumbnail = event.thumbnail || {};
    const url = absoluteUrl(event.url);

    return withContext({
        '@type': 'Event',
        '@id': `${url}#event`,
        name: event.name,
        url,
        description: stripHtml(event.description || seo.description),
        image: images(event.mainImage || thumbnail.landscape),
        startDate: date(props.start_date),
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        eventStatus: 'https://schema.org/EventScheduled',
        location: event.venues && { '@type': 'Place', name: stripHtml(event.venues) },
        organizer: organizationRef(),
        offers: offer({
            id: `${url}#ticket-offer`,
            url,
            low: number(props.start_price),
            high: number(props.end_price)
        })
    });
}

export function getVenueStructuredData(venue) {
    if (!venue || !venue.name) return null;

    const heroImage = venue.heroImage || {};
    const name = splitVenueName(venue.name);
    const seo = venue.seo || {};
    const thumbnail = venue.thumbnail || {};
    const url = absoluteUrl(venue.url);

    return withContext({
        '@type': ['PerformingArtsTheater', 'EntertainmentBusiness'],
        '@id': venueId(venue),
        name: name.primary,
        alternateName: name.alternate,
        url,
        description: stripHtml(venue.description || seo.description),
        telephone: venue.phone || '+64 9 309 2677',
        image: images(heroImage.desktop || thumbnail.landscape),
        address: venue.addressStructuredData,
        geo: venue.geoStructuredData,
        parentOrganization: organizationRef(),
        sameAs: VENUE_SAME_AS[venue.slug],
        additionalProperty: venue.openHours && {
            '@type': 'PropertyValue',
            name: 'Visitor information',
            value: stripHtml(venue.openHours)
        }
    });
}

export function getContentBlocksStructuredData(blocks = [], page = {}) {
    const url = absoluteUrl(page.url) || ROOT_URL;
    const nodes = [];
    const faqBlocks = [];

    (Array.isArray(blocks) ? blocks : []).forEach((block, index) => {
        if (block.type === 'faq') {
            faqBlocks.push(block);
            return;
        }
        if (block.type === 'video') {
            nodes.push(video(block, index, url, page.title));
        }
    });

    if (faqBlocks.length) {
        const questions = faqBlocks.reduce((items, block) => {
            const entity = faq(block, 0, url, page.title);
            return entity && entity.mainEntity ? items.concat(entity.mainEntity) : items;
        }, []);

        if (questions.length) {
            nodes.unshift(compact({
                '@type': 'FAQPage',
                '@id': `${url}#faq`,
                url,
                name: page.title,
                inLanguage: LANGUAGE,
                mainEntity: questions
            }));
        }
    }

    return graph(nodes);
}

function organization() {
    return {
        '@type': 'Organization',
        '@id': ORG_ID,
        name: APP_NAME,
        url: APP_HOSTNAME,
        logo: {
            '@type': 'ImageObject',
            '@id': LOGO_ID,
            url: absoluteUrl(LOGO_PATH)
        },
        description: APP_DESCRIPTION,
        slogan: 'Creating memories that last a lifetime',
        telephone: '+64 9 309 2677',
        email: 'online@aucklandlive.co.nz',
        address: {
            '@type': 'PostalAddress',
            streetAddress: 'Level 4, Aotea Centre, 50 Mayoral Drive',
            addressLocality: 'Auckland',
            postalCode: '1010',
            addressCountry: 'NZ'
        },
        contactPoint: [
            {
                '@type': 'ContactPoint',
                contactType: 'Customer Service',
                telephone: '+64 9 309 2677',
                email: 'online@aucklandlive.co.nz',
                areaServed: 'NZ',
                availableLanguage: 'English'
            },
            {
                '@type': 'ContactPoint',
                contactType: 'Ticket Sales',
                telephone: '0800 111 999',
                areaServed: 'NZ',
                availableLanguage: 'English'
            }
        ],
        parentOrganization: PARENT_ORG,
        areaServed: {
            '@type': 'AdministrativeArea',
            name: 'Auckland, Tāmaki Makaurau'
        },
        sameAs: SOCIAL_URLS
    };
}

function organizationRef() {
    return { '@id': ORG_ID };
}

function publisher() {
    return {
        '@type': 'Organization',
        '@id': ORG_ID,
        name: APP_NAME,
        url: ROOT_URL,
        logo: {
            '@type': 'ImageObject',
            '@id': LOGO_ID,
            url: absoluteUrl(LOGO_PATH)
        },
        parentOrganization: PARENT_ORG
    };
}

function performanceEvent(show, performance, url) {
    const seo = show.seo || {};
    const specifications = show.specifications || {};
    const key = performanceKey(performance);

    return compact({
        '@type': eventTypes(show.genre),
        '@id': `${url}#performance-${key}`,
        name: show.name,
        url,
        description: stripHtml(seo.description || show.description),
        image: images((show.heroImage || {}).desktop || (show.thumbnail || {}).landscape),
        startDate: date(performance.startsAt),
        doorTime: date(performance.doorsOpenAt),
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        eventStatus: 'https://schema.org/EventScheduled',
        location: performanceVenueRef(performance),
        organizer: [
            show.presenter && { '@type': 'Organization', name: stripHtml(show.presenter) },
            organizationRef()
        ],
        performer: performers(show),
        audience: specifications['age-restriction'] && {
            '@type': 'Audience',
            audienceType: specifications['age-restriction']
        },
        offers: offer({
            id: `${url}#offer-${key}`,
            url: performance.bookingLink || url,
            low: number(performance.props.price_lowest),
            high: number(performance.props.price_highest),
            availability: performance.isSoldOut ?
                'https://schema.org/SoldOut' :
                performance.isSellingFast ?
                    'https://schema.org/LimitedAvailability' :
                    'https://schema.org/InStock',
            allowWithoutPrice: true
        })
    });
}

function performanceVenueRef(performance) {
    const venue = performance.getVenueObject();
    if (!venue) {
        return performance.venue ? { '@type': 'Place', name: performance.venue } : undefined;
    }

    return { '@id': venueId(venue) };
}

function venuePlace(venue) {
    if (!venue) return null;

    return compact({
        '@type': ['PerformingArtsTheater', 'EntertainmentBusiness'],
        '@id': venueId(venue),
        name: splitVenueName(venue.name).primary,
        url: absoluteUrl(venue.url),
        address: venue.addressStructuredData,
        geo: venue.geoStructuredData,
        sameAs: VENUE_SAME_AS[venue.slug]
    });
}

function performers(show) {
    const artists = String((show.specifications || {}).artists || '')
        .split(/,|;|\band\b/gi)
        .map((name) => stripHtml(name).trim())
        .filter(Boolean)
        .map((name) => ({ '@type': 'Person', name }));

    return compact([
        show.company && { '@type': 'PerformingGroup', name: stripHtml(show.company) },
        ...artists
    ]);
}

function faq(block, index, url, title) {
    const questions = (block.data || [])
        .filter((item) => item.title && item.content)
        .map((item) => ({
            '@type': 'Question',
            name: stripHtml(item.title),
            acceptedAnswer: {
                '@type': 'Answer',
                text: stripHtml(item.content)
            }
        }));

    return questions.length && compact({
        '@type': 'FAQPage',
        '@id': `${url}#faq-${index + 1}`,
        url,
        name: block.data && block.data[0] && block.data[0].heading || title,
        inLanguage: LANGUAGE,
        mainEntity: questions
    });
}

function video(block, index, url, title) {
    const data = block.data || {};
    const parsed = getVideoId(data.video_url || '');
    const embedUrl = videoEmbedUrl(parsed);

    return data.title && embedUrl && compact({
        '@type': 'VideoObject',
        '@id': `${url}#video-${index + 1}`,
        name: data.title,
        description: stripHtml(data.description || data.credit || title),
        embedUrl,
        thumbnailUrl: parsed.service === 'youtube' ? `https://img.youtube.com/vi/${parsed.id}/hqdefault.jpg` : undefined,
        publisher: organizationRef()
    });
}

function videoEmbedUrl(videoData) {
    if (!videoData || !videoData.id) return undefined;
    if (videoData.service === 'youtube') return `https://www.youtube.com/embed/${videoData.id}`;
    if (videoData.service === 'vimeo') return `https://player.vimeo.com/video/${videoData.id}`;
    return undefined;
}

function offer({ id, url, low, high, availability = 'https://schema.org/InStock', allowWithoutPrice = false }) {
    if (low === undefined && high === undefined && !allowWithoutPrice) return null;

    const isRange = low !== undefined && high !== undefined && low !== high;
    const schema = {
        '@type': isRange ? 'AggregateOffer' : 'Offer',
        '@id': id,
        url,
        priceCurrency: 'NZD',
        availability,
        seller: organizationRef()
    };

    if (isRange) {
        schema.lowPrice = low.toFixed(2);
        schema.highPrice = high.toFixed(2);
    } else if (low !== undefined || high !== undefined) {
        schema.price = (low === undefined ? high : low).toFixed(2);
    }

    return schema;
}

function eventTypes(genre) {
    const types = ['Event', 'PerformingArtsEvent'];
    const normalized = String(genre || '').toLowerCase();
    const match = GENRES.find((item) => item.schemaType && (
        normalized === item.value ||
        normalized === String(item.label).toLowerCase() ||
        normalized.includes(String(item.label).toLowerCase())
    ));

    if (match && match.schemaType) types.push(match.schemaType);
    return types;
}

function venueId(venue) {
    return `${APP_HOSTNAME}/venue/${venue.slug}#venue`;
}

function splitVenueName(value = '') {
    const parts = String(value).split('|').map((part) => part.trim()).filter(Boolean);
    return { primary: parts[0] || value, alternate: parts[1] };
}

function images(value) {
    const image = absoluteUrl(value);
    return Array.isArray(image) ? image : image && [image];
}

function number(value) {
    return value === undefined || value === null || value === '' || Number.isNaN(Number(value)) ?
        undefined :
        Number(value);
}

function date(value) {
    return value && DateHelper.getDateWithNZTimezone(value).format();
}

function performanceKey(performance) {
    return performance.id || DateHelper.getDateWithNZTimezone(performance.startsAt).format('YYYYMMDD-HHmm');
}

function withContext(schema) {
    return compact({ '@context': 'https://schema.org', ...schema });
}

function graph(nodes) {
    const data = compact(nodes);
    return data && data.length ? { '@context': 'https://schema.org', '@graph': data } : null;
}

function compact(value) {
    if (Array.isArray(value)) return value.map((item) => compact(item)).filter((item) => !empty(item));

    if (value && typeof value === 'object') {
        return Object.keys(value).reduce((result, key) => {
            const item = compact(value[key]);
            if (!empty(item)) result[key] = item;
            return result;
        }, {});
    }

    return value;
}

function empty(value) {
    return value === undefined ||
        value === null ||
        value === '' ||
        Array.isArray(value) && !value.length ||
        Boolean(value && typeof value === 'object' && !Array.isArray(value) && !Object.keys(value).length);
}

function decodeEntities(value) {
    return String(value).replace(
        /&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z][a-zA-Z0-9]+);/g,
        (match, entity) => {
            if (entity[0] === '#') {
                const codePoint = entity[1] === 'x' || entity[1] === 'X' ?
                    parseInt(entity.slice(2), 16) :
                    parseInt(entity.slice(1), 10);

                if (Number.isNaN(codePoint)) {
                    return match;
                }

                try {
                    return String.fromCodePoint(codePoint);
                } catch (error) {
                    return match;
                }
            }

            const named = {
                amp: '&',
                lt: '<',
                gt: '>',
                quot: '"',
                apos: "'",
                nbsp: ' ',
                eacute: 'é',
                Eacute: 'É',
                rsquo: '\u2019',
                lsquo: '\u2018',
                rdquo: '\u201d',
                ldquo: '\u201c',
                mdash: '\u2014',
                ndash: '\u2013',
                hellip: '\u2026'
            };

            return Object.prototype.hasOwnProperty.call(named, entity) ? named[entity] : match;
        }
    );
}
